import React, { useCallback, useState } from "react";
import { message, Modal, Slider } from "antd";
import Cropper, { Area } from "react-easy-crop";
import { useTranslation } from "react-i18next";

export type CropShape = "rectangle" | "square" | null;

interface ResizableCropModalProps {
  open: boolean;
  imageUrl: string;
  onCancel: () => void;
  onOk: (croppedImageBlob: Blob) => void;
  /**
   * "rectangle" uses 16:9.
   * "square" uses 1:1.
   * null/undefined uses 1:1.
   */
  shape?: CropShape;
}

const ASPECT_RATIO = {
  rectangle: 16 / 9,
  square: 1,
} as const;

const MAX_OUTPUT_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.crossOrigin = "anonymous";
    image.src = url;
  });

const getCroppedImage = async (
  imageUrl: string,
  cropPixels: Area,
): Promise<Blob | null> => {
  const image = await createImage(imageUrl);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const scale = Math.min(
    1,
    MAX_OUTPUT_DIMENSION / Math.max(cropPixels.width, cropPixels.height),
  );
  const outputWidth = Math.round(cropPixels.width * scale);
  const outputHeight = Math.round(cropPixels.height * scale);

  canvas.width = outputWidth;
  canvas.height = outputHeight;
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  return new Promise<Blob | null>((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas export failed"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    } catch (error) {
      reject(error);
    }
  });
};

const ResizableCropModal: React.FC<ResizableCropModalProps> = ({
  open,
  imageUrl,
  onCancel,
  onOk,
  shape,
}) => {
  const aspect =
    shape === "rectangle" ? ASPECT_RATIO.rectangle : ASPECT_RATIO.square;
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    [],
  );

  const handleCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedBlob = await getCroppedImage(imageUrl, croppedAreaPixels);
      if (croppedBlob) {
        onOk(croppedBlob);
      }
    } catch {
      messageApi.warning(
        t("message.warning.crop_edit_upload") ||
          "Cannot edit this image from the server. Re-upload the image to crop it.",
      );
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        title={t("label.edit_image")}
        onCancel={onCancel}
        onOk={handleCrop}
        okText={t("button.done")}
        cancelText={t("button.cancel")}
        width="90%"
        style={{ maxWidth: "800px" }}
        styles={{
          body: {
            padding: 16,
            maxHeight: "min(70vh, 760px)",
            overflow: "hidden",
          },
        }}
        destroyOnHidden
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "min(55vh, 520px)",
            backgroundColor: "#f0f0f0",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {imageUrl ? (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
            />
          ) : null}
        </div>
        <div style={{ marginTop: 16 }}>
          <Slider
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={setZoom}
            tooltip={{ formatter: null }}
          />
        </div>
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            textAlign: "center",
            color: "#666",
          }}
        >
          {t("label.crop_instructions_fixed") ||
            "Drag the image to reposition it. Use the slider to zoom."}
        </div>
      </Modal>
    </>
  );
};

export default ResizableCropModal;
